import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { canvasApi } from "@/lib/api/canvas";
import type {
  CanvasForm,
  CanvasFormsResponse,
  CanvasStats,
  FormAction,
  CreateCanvasFormData,
  UpdateCanvasFormData,
  WorkflowActionData,
} from "@/lib/api/canvas";

import { withQuerySignal } from "@/lib/api";
// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all canvas forms with pagination and filters
 */
export function useCanvasForms(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  projectId?: number;
}) {
  return useQuery<CanvasFormsResponse>({
    queryKey: queryKeys.canvas.list(params),
    queryFn: ({ signal }) => withQuerySignal(signal, () => canvasApi.getAll(params)),
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch simple list for dropdowns
 */
export function useCanvasList() {
  return useQuery<
    {
      id: number;
      number: string;
      status: string | null;
      resourceRequestId: number;
    }[]
  >({
    queryKey: queryKeys.canvas.dropdown(),
    queryFn: () => canvasApi.getList(),
  });
}

/**
 * Fetch statistics by status
 */
export function useCanvasStats() {
  return useQuery<CanvasStats[]>({
    queryKey: queryKeys.canvas.stats(),
    queryFn: () => canvasApi.getStats(),
  });
}

/**
 * Fetch single canvas form by ID
 */
export function useCanvasForm(id: number) {
  return useQuery<CanvasForm>({
    queryKey: queryKeys.canvas.detail(id),
    queryFn: () => canvasApi.getById(id),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch canvas form by number
 */
export function useCanvasFormByNumber(number: string) {
  return useQuery<CanvasForm>({
    queryKey: [...queryKeys.canvas.all, "by-number", number],
    queryFn: () => canvasApi.getByNumber(number),
    enabled: !!number,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch PDF document URL
 */
export function useCanvasDocument(id: number) {
  return useQuery<{ url: string | null }>({
    queryKey: queryKeys.canvas.document(id),
    queryFn: () => canvasApi.getDocument(id),
    enabled: !!id,
  });
}

/**
 * Fetch workflow history
 */
export function useCanvasHistory(id: number) {
  return useQuery<FormAction[]>({
    queryKey: queryKeys.canvas.history(id),
    queryFn: () => canvasApi.getHistory(id),
    enabled: !!id,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create new canvas form
 */
export function useCreateCanvasForm() {
  const queryClient = useQueryClient();

  return useMutation<CanvasForm, Error, CreateCanvasFormData>({
    mutationFn: (data) => canvasApi.create(data),
    onSuccess: () => {
      // Invalidate all canvas lists
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.stats() });
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.dropdown() });
    },
  });
}

/**
 * Update canvas form (draft only)
 */
export function useUpdateCanvasForm() {
  const queryClient = useQueryClient();

  return useMutation<CanvasForm, Error, { id: number; data: UpdateCanvasFormData }>({
    mutationFn: ({ id, data }) => canvasApi.update(id, data),
    onSuccess: (data) => {
      // Invalidate specific canvas and all lists
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.document(data.id) });
    },
  });
}

/**
 * Delete canvas form (draft only, never submitted)
 */
export function useDeleteCanvasForm() {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, number>({
    mutationFn: (id) => canvasApi.delete(id),
    onSuccess: (_, id) => {
      // Invalidate all canvas queries
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.stats() });
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.dropdown() });
      // Remove specific canvas from cache
      queryClient.removeQueries({ queryKey: queryKeys.canvas.detail(id) });
    },
  });
}

// ============================================================================
// Workflow Mutation Hooks
// ============================================================================

/**
 * Submit canvas for review (draft → pending)
 */
export function useSubmitCanvasForm() {
  const queryClient = useQueryClient();

  return useMutation<CanvasForm, Error, number>({
    mutationFn: (id) => canvasApi.submit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Review canvas form (pending → reviewed)
 */
export function useReviewCanvasForm() {
  const queryClient = useQueryClient();

  return useMutation<CanvasForm, Error, { id: number; data: WorkflowActionData }>({
    mutationFn: ({ id, data }) => canvasApi.review(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Approve canvas form (reviewed → approved)
 */
export function useApproveCanvasForm() {
  const queryClient = useQueryClient();

  return useMutation<CanvasForm, Error, { id: number; data: WorkflowActionData }>({
    mutationFn: ({ id, data }) => canvasApi.approve(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Reject canvas form (pending/reviewed → rejected)
 */
export function useRejectCanvasForm() {
  const queryClient = useQueryClient();

  return useMutation<CanvasForm, Error, { id: number; comment: string }>({
    mutationFn: ({ id, comment }) => canvasApi.reject(id, { comment }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Return canvas to draft (pending/reviewed → draft)
 */
export function useReturnToDraftCanvasForm() {
  const queryClient = useQueryClient();

  return useMutation<CanvasForm, Error, { id: number; comment: string }>({
    mutationFn: ({ id, comment }) => canvasApi.returnToDraft(id, { comment }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Mark canvas as completed (approved → completed)
 */
export function useCompleteCanvasForm() {
  const queryClient = useQueryClient();

  return useMutation<CanvasForm, Error, number>({
    mutationFn: (id) => canvasApi.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.canvas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}
